import { useCallback } from "react";
import {
  findFromGatewayServiceUsersList,
  findFromServiceUsersList,
  findFromUsersList,
} from "@src/components/organisms/user-management-page-search/user-management-list-search";
import {
  ZitadelUser,
  ZitadelServiceUser,
  ZitadelGatewayServiceUser,
  UserManagementListActiveViewType,
  UserManagementListActiveRoleEnum,
  UserManagementListActiveRoleRenderDataType,
} from "@src/types/user-management";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import {
  updateUserManagementSearchQuery,
  updateUserManagementSearchTerms,
} from "@src/reducers/your-reducer";

type UseUserManagementPageSearchParams = {
  userManagementListActiveView: UserManagementListActiveViewType;
};

type UseUserManagementPageSearchResult = {
  userManagementSearchTerms: string[];
  userManagementSearchQuery: string;
  onSearchTermsChange: (userManagementSearchTerms: string[]) => void;
  onSearchQueryChange: (userManagementSearchQuery: string) => void;
  findSearchedListForActiveView: (
    userManagementListActiveRoleData: UserManagementListActiveRoleRenderDataType,
    userManagementSearchTerms: string[],
  ) => UserManagementListActiveRoleRenderDataType;
};

type RunSearchForActiveViewParams = {
  userManagementListActiveView: UserManagementListActiveViewType;
  userManagementListActiveRoleData: UserManagementListActiveRoleRenderDataType;
  nextSearchTerms: string[];
};

// Centralizes the search logic for the user management page, delegating to specific search functions based on the active view.
const runSearchForActiveView = ({
  userManagementListActiveView,
  userManagementListActiveRoleData,
  nextSearchTerms,
}: RunSearchForActiveViewParams): UserManagementListActiveRoleRenderDataType => {
  switch (userManagementListActiveView) {
    case UserManagementListActiveRoleEnum.USERS:
      return findFromUsersList(
        userManagementListActiveRoleData as ZitadelUser[],
        nextSearchTerms,
      );
    case UserManagementListActiveRoleEnum.SERVICE_USERS:
      return findFromServiceUsersList(
        userManagementListActiveRoleData as ZitadelServiceUser[],
        nextSearchTerms,
      );
    case UserManagementListActiveRoleEnum.GATEWAY_SERVICE_USERS:
      return findFromGatewayServiceUsersList(
        userManagementListActiveRoleData as ZitadelGatewayServiceUser[],
        nextSearchTerms,
      );
    default:
      return findFromUsersList(
        userManagementListActiveRoleData as ZitadelUser[],
        nextSearchTerms,
      );
  }
};

export const useUserManagementPageSearchHook = ({
  // active role name (users, service users, or gateway service users)
  userManagementListActiveView,
}: UseUserManagementPageSearchParams): UseUserManagementPageSearchResult => {
  const dispatch = useDispatch();

  const userManagementSearchTerms = useSelector(
    (state: RootState) => state.yourSlice.userManagementSearchTerms,
  );
  const userManagementSearchQuery = useSelector(
    (state: RootState) => state.yourSlice.userManagementSearchQuery,
  );

  const handleSearchTermsChange = useCallback(
    (userManagementSearchTerms: string[]) => {
      dispatch(updateUserManagementSearchTerms(userManagementSearchTerms));
    },
    [dispatch],
  );

  const handleSearchQueryChange = useCallback(
    (userManagementSearchQuery: string) => {
      dispatch(updateUserManagementSearchQuery(userManagementSearchQuery));
    },
    [dispatch],
  );

  const findSearchedListForActiveView = useCallback(
    (
      userManagementListActiveRoleData: UserManagementListActiveRoleRenderDataType,
      nextSearchTerms: string[],
    ) =>
      runSearchForActiveView({
        userManagementListActiveView,
        userManagementListActiveRoleData,
        nextSearchTerms,
      }),

    [userManagementListActiveView],
  );

  return {
    userManagementSearchTerms,
    userManagementSearchQuery,
    onSearchTermsChange: handleSearchTermsChange,
    onSearchQueryChange: handleSearchQueryChange,
    findSearchedListForActiveView,
  };
};
